import React, { useState } from 'react';
import Box from '@cloudscape-design/components/box';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { ThreatGroup } from '@utils/threatFormatter';

export interface ThreatSummaryDashboardProps {
  threatGroups: ThreatGroup[];
}

export const ThreatSummaryDashboard: React.FC<ThreatSummaryDashboardProps> = ({
  threatGroups,
}) => {
  const [expandedSeverities, setExpandedSeverities] = useState<Set<string>>(
    new Set(threatGroups.length > 0 ? [threatGroups[0].severity] : [])
  );

  const toggleSeverity = (severity: string) => {
    const newExpanded = new Set(expandedSeverities);
    if (newExpanded.has(severity)) {
      newExpanded.delete(severity);
    } else {
      newExpanded.add(severity);
    }
    setExpandedSeverities(newExpanded);
  };

  if (threatGroups.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ borderBottom: '2px solid #1976d2', paddingBottom: '8px', marginBottom: '16px' }}>
        <Box variant="h2">Threats</Box>
      </div>

      {/* Severity Groups */}
      <SpaceBetween size="s">
        {threatGroups.map((group) => (
          <ExpandableSection
            key={group.severity}
            variant="container"
            headerText={`${group.severity} (${group.threats.length} Threat Type${group.threats.length !== 1 ? 's' : ''}, ${group.totalOccurrences} occurrence${group.totalOccurrences !== 1 ? 's' : ''})`}
            defaultExpanded={expandedSeverities.has(group.severity)}
            onChange={() => toggleSeverity(group.severity)}
          >
            <SpaceBetween size="s">
              {group.threats.map((threat, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#ffffff',
                    border: `1px solid #e0e0e0`,
                    borderLeft: `4px solid ${getSeverityColor(group.severity)}`,
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '16px', 
                    justifyContent: 'space-between',
                    width: '100%',
                    flexWrap: 'nowrap'
                  }}>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: '#1a1a1a',
                      whiteSpace: 'nowrap',
                      fontSize: '14px'
                    }}>
                      {threat.type}
                    </span>
                    <span style={{ 
                      color: '#424242', 
                      whiteSpace: 'nowrap', 
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      {threat.occurrences} occurrence{threat.occurrences !== 1 ? 's' : ''} • {threat.characters} character{threat.characters !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <Box variant="p" fontSize="body-s" margin={{ top: 'xs' }} color="text-body-secondary">
                    {threat.description}
                  </Box>
                </div>
              ))}
            </SpaceBetween>
          </ExpandableSection>
        ))}
      </SpaceBetween>
    </div>
  );
};

function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'CRITICAL':
      return '#d32f2f';
    case 'HIGH':
      return '#f57c00';
    case 'MEDIUM':
      return '#fbc02d';
    case 'LOW':
      return '#1976d3';
    default:
      return '#9e9e9e';
  }
}
